import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { BuyerPortalSettingsViewModel } from '../../services/backendServices/ViewModels';

export type ReferenceDataResource = 'vendor' | 'gl' | 'customDataset' | 'user' | 'userGroup';

export interface ReferenceDataCacheVersions {
    getVersion(resource: ReferenceDataResource, datasetName?: string): string | undefined;
}

type ReferenceDataVersionSettings = Pick<
    BuyerPortalSettingsViewModel,
    | 'VendorLastUpdateDate'
    | 'CompanyGLAccountLastUpdateDate'
    | 'CompanyCustomDataset'
    | 'UserLastUpdateDate'
    | 'UserGroupLastUpdateDate'
>;

const ReferenceDataCacheVersionContext = createContext<ReferenceDataCacheVersions | null>(null);

export function createCacheVersion(userId: number, marker: string | null | undefined) {
    // undefined = settings not ready, skip cache. null = no date yet, still cacheable.
    return marker === undefined ? undefined : `${userId}:${marker ?? 'empty'}`;
}

function createReferenceDataCacheVersions(
    settings: ReferenceDataVersionSettings | null | undefined,
    userId: number | null | undefined
): ReferenceDataCacheVersions | null {
    if (!settings || userId == null) {
        return null;
    }

    return {
        getVersion(resource, datasetName) {
            if (resource === 'vendor') {
                return createCacheVersion(userId, settings.VendorLastUpdateDate);
            }

            if (resource === 'gl') {
                return createCacheVersion(userId, settings.CompanyGLAccountLastUpdateDate);
            }

            if (resource === 'user') {
                return createCacheVersion(userId, settings.UserLastUpdateDate);
            }

            if (resource === 'userGroup') {
                return createCacheVersion(userId, settings.UserGroupLastUpdateDate);
            }

            if (!Array.isArray(settings.CompanyCustomDataset)) {
                return undefined;
            }

            const marker =
                settings.CompanyCustomDataset.find((dataset) => dataset.DatasetName === datasetName)?.LastUpdateDate ??
                null;

            return createCacheVersion(userId, marker);
        },
    };
}

export function ReferenceDataCacheVersionProvider({
    settings,
    userId,
    children,
}: PropsWithChildren<{
    settings: ReferenceDataVersionSettings | null | undefined;
    userId: number | null | undefined;
}>) {
    const versions = useMemo(() => createReferenceDataCacheVersions(settings, userId), [settings, userId]);

    return (
        <ReferenceDataCacheVersionContext.Provider value={versions}>
            {children}
        </ReferenceDataCacheVersionContext.Provider>
    );
}

export function useReferenceDataCacheVersions() {
    return useContext(ReferenceDataCacheVersionContext);
}

export function useReferenceDataCacheVersion(resource: ReferenceDataResource, datasetName?: string) {
    return useReferenceDataCacheVersions()?.getVersion(resource, datasetName);
}
